// The AI helper in the composer: asks the server for 2-3 suggested replies, shows them as
// cards. Nothing goes out until a person clicks Use (edit first) or Send (right away, through
// the normal send path). The AI never sends by itself.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { countSegments, describeUcs2Triggers } from '@bt/core';
import { api, ApiError, errorMessage, type AiSuggestion } from '../../api';
import { pictureOf, usePictures } from '../../components/PicturePicker';

export interface AiChoice {
  /** the ai_suggestions row */
  id: string;
  index: number;
}

export function AiPanel({
  conversationId,
  busy,
  onUse,
  onSend,
  onClose,
}: {
  conversationId: string;
  /** true while the composer is sending; the buttons wait */
  busy: boolean;
  onUse: (s: AiSuggestion, choice: AiChoice, thumbUrl: string | null) => void;
  onSend: (s: AiSuggestion, choice: AiChoice, thumbUrl: string | null) => Promise<void>;
  onClose: () => void;
}) {
  const [hint, setHint] = useState('');
  const [thinking, setThinking] = useState(false);
  const [result, setResult] = useState<{ id: string; suggestions: AiSuggestion[] } | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [sendingIndex, setSendingIndex] = useState<number | null>(null);
  const pictures = usePictures();

  // attach_asset_id -> a URL for the thumbnail (the server may already send attach_url)
  const thumbFor = (s: AiSuggestion): string | null => {
    if (s.attach_url) return s.attach_url;
    if (!s.attach_asset_id) return null;
    const item = pictures.data?.items.find((i) => i.asset_id === s.attach_asset_id);
    return item ? (pictureOf(item)?.url ?? null) : null;
  };

  const ask = async () => {
    setThinking(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.ai.suggest(conversationId, hint.trim() || null);
      setResult({ id: r.id, suggestions: r.suggestions ?? [] });
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'error';
      const status = e instanceof ApiError ? e.status : 0;
      setError({ code: status === 503 ? 'ai_not_configured' : code, message: errorMessage(e) });
    } finally {
      setThinking(false);
    }
  };

  const send = async (s: AiSuggestion, i: number) => {
    if (!result) return;
    setSendingIndex(i);
    try {
      await onSend(s, { id: result.id, index: i }, thumbFor(s));
      setResult(null);
    } finally {
      setSendingIndex(null);
    }
  };

  return (
    <div className="ai-panel">
      <div className="ai-head">
        <input
          className="input"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="Tell the AI what to say (optional)"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!thinking) void ask();
            }
          }}
          disabled={thinking}
        />
        <button className="btn small primary" onClick={() => void ask()} disabled={thinking}>
          {thinking ? 'Thinking' : result ? 'Again' : 'Suggest'}
        </button>
        <button className="btn small ghost" onClick={onClose} aria-label="Close AI">
          Close
        </button>
      </div>

      {thinking && (
        <div className="muted small">
          <span className="spinner" aria-hidden="true" /> thinking…
        </div>
      )}

      {error && error.code === 'ai_not_configured' && (
        <div className="notice warn">
          AI is not set up yet. Add the key in .env and restart. <span className="muted">(ANTHROPIC_API_KEY or OPENAI_API_KEY)</span>
        </div>
      )}
      {error && error.code === 'ai_disabled' && (
        <div className="notice warn">
          The AI helper is switched off. An admin can turn it on under <Link to="/settings/ai">Settings › AI</Link>.
        </div>
      )}
      {error && error.code !== 'ai_not_configured' && error.code !== 'ai_disabled' && <div className="notice danger">{error.message}</div>}

      {result && result.suggestions.length === 0 && <div className="muted small">The AI had nothing to suggest. Try a hint.</div>}
      {result && result.suggestions.length > 0 && (
        <div className="ai-cards">
          {result.suggestions.map((s, i) => {
            const thumb = thumbFor(s);
            return (
              <div key={i} className="ai-card">
                {thumb && <img className="thumb" src={thumb} alt="attached picture" />}
                <div className="text">{s.text}</div>
                {s.why && <div className="why">{s.why}</div>}
                {/* WHY (review round 2): Send here goes straight out. Without a count the office
                    never sees that a 108-character AI reply is leaving as 2 segments. */}
                <SegmentNote text={s.text} />
                <div className="actions">
                  <button className="btn small" disabled={busy || sendingIndex != null} onClick={() => onUse(s, { id: result.id, index: i }, thumb)}>
                    Use
                  </button>
                  <button className="btn small primary" disabled={busy || sendingIndex != null} onClick={() => void send(s, i)}>
                    {sendingIndex === i ? 'Sending' : 'Send'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {result && <div className="tiny faint" style={{ marginTop: 6 }}>Suggestions only. Nothing is sent until you click Send.</div>}
    </div>
  );
}

/** the same count and the same words as the composer, under every card */
function SegmentNote({ text }: { text: string }) {
  const info = countSegments(text.trim());
  const reason = info.encoding === 'ucs2' && info.ucs2Triggers.length > 0 ? describeUcs2Triggers(text.trim(), info.ucs2Triggers) : null;
  const cls = info.segments >= 3 ? 'danger-text' : info.segments === 2 ? 'warn-text' : 'faint';
  return (
    <div className={`tiny ${cls}`}>
      {info.length} / {info.capacity} · {info.segments} {info.segments === 1 ? 'segment' : 'segments'}
      {reason && <> · {reason}</>}
    </div>
  );
}
